import React from 'react';
import { Package, Info } from 'lucide-react';
import type { Product } from '../../types';

interface Props {
  product: Product;
}

/**
 * Visualizzazione "Sconto Scatola" — fonte ERP.
 *
 * Mostra la configurazione corrente (q.tà per scatola e % sconto) con
 * un'anteprima del prezzo unitario e del risparmio.
 */
export function BoxDiscountEditor({ product }: Props) {
  const boxQty = product.boxQty;
  const discountPct = product.boxDiscountPct;
  const hasConfig = !!(boxQty && discountPct);
  const listino = product.listino10 || 0;
  const showPreview = !!(hasConfig && listino > 0 && discountPct < 100);
  const pricePerUnit = showPreview ? listino * (1 - (discountPct as number) / 100) : 0;
  const totalBox = pricePerUnit * (boxQty || 0);
  const risparmioBox = showPreview ? listino * (boxQty as number) - totalBox : 0;

  return (
    <div className="rounded-xl border bg-white shadow-sm overflow-hidden" data-testid="admin-box-discount-editor">
      <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
        <Package className="w-4 h-4 text-amber-600" />
        <h2 className="text-sm font-semibold tracking-tight">Sconto Scatola</h2>
        {hasConfig && (
          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">
            Attivo su ERP
          </span>
        )}
      </div>

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Q.tà per scatola</div>
            <div className="font-semibold tabular-nums" data-testid="admin-box-qty-value">{boxQty ?? <span className="text-muted-foreground font-normal">—</span>}</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">% Sconto scatola</div>
            <div className="font-semibold tabular-nums" data-testid="admin-box-pct-value">
              {discountPct != null ? `${discountPct}%` : <span className="text-muted-foreground font-normal">—</span>}
            </div>
          </div>
        </div>

        {showPreview && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs space-y-1">
            <div className="flex items-center gap-1.5 text-emerald-900 font-semibold">
              <Info className="w-3.5 h-3.5" />
              Anteprima
            </div>
            <div className="grid grid-cols-2 gap-x-4 text-emerald-900">
              <div>Prezzo singolo (listino): <span className="font-semibold tabular-nums">€ {listino.toFixed(2).replace('.', ',')}</span></div>
              <div>Prezzo a pezzo (scatola): <span className="font-semibold tabular-nums">€ {pricePerUnit.toFixed(2).replace('.', ',')}</span></div>
              <div>Sconto applicato: <span className="font-semibold">-{Math.round(discountPct as number)}%</span></div>
              <div>Risparmio per scatola: <span className="font-semibold tabular-nums">€ {risparmioBox.toFixed(2).replace('.', ',')}</span></div>
            </div>
            <div className="text-emerald-800 mt-1">Totale scatola ({boxQty} pz): <span className="font-semibold tabular-nums">€ {totalBox.toFixed(2).replace('.', ',')}</span></div>
          </div>
        )}
      </div>
    </div>
  );
}
